import * as OpAmpTPCDatabase from './op-amp-tpc-database.json'; 
import * as DifferentialAmpTPCDatabase from './differential-amp-tpc-database.json'; 
import * as InAmpTPCDatabase from './in-amp-tpc-database.json';
import * as AdcTPCDatabase from './adc-tpc-database.json';
import * as AdcCustomDatabase from './adc-custom-database.js';
import * as InAmpCustomDatabase from './in-amp-custom-database.js';
import * as OpAmpCustomDatabase from './op-amp-custom-database.js';
import * as DifferentialAmpCustomDatabase from './differential-amp-custom-database.js';
import * as ReferenceCustomDatabase from './reference-custom-database.js';
import * as InputProtectionCustomDatabase from './input-protection-custom-database.js';
import * as RemovedParts from './removed-parts-database.js';
import { COMPONENT_NAMES } from '../sim-constants';

let OpAmpPDB = { data: null };
let DifferentialAmpPDB = { data: null };
let AdcPDB = { data: null };
let InAmpPDB = { data: null };
let ReferencePDB = { data: null };

export const PART_TYPES = {
    ADC: "adc",
    OP_AMP: "opAmp",
    TIA_OP_AMP: "transimpedanceAmp",
    DIFF_AMP: "differentialAmp",
    IN_AMP: "inAmp",
    REFERENCE: "reference",
    DIODE : "diode"
}

// setters
/**
 * Initialize Op Amps with CDP data.
 * @param {Object[]} partData A list of parts that use the Simulation Library's expected format: parameter 0 is "Part Number", 4455 is "cCm", and so on.
 */
 export function setOpAmpData(partData) {
    // Converting the data is the responsibility of the calling application
    OpAmpPDB.data = partData;
    let filteredData = partData.filter(p=>isPartAllowed(p["Part Number"], PART_TYPES.OP_AMP));
    OpAmpPDB.data = filteredData;    
}

/**
 * Initialize Differential Amps with CDP data.
 * @param {Object[]} partData A list of Parts that use the Simulation Library's expected format: parameter 0 is "Part Number", 4101 is "vNoise", and so on.
 */
 export function setDifferentialAmpData(partData) {
    // Converting the data is the responsibility of the calling application
    DifferentialAmpPDB.data = partData;
    let filteredData = partData.filter(p=>isPartAllowed(p["Part Number"], PART_TYPES.DIFF_AMP));
    DifferentialAmpPDB.data = filteredData; 
    
}

/**
 * Initialize ADCs with CDP data.
 * @param {Object[]} partData A list of ADCs that use the Simulation Library's expected format: parameter 0 is "Part Number", 4907 is "SNR", and so on.
 */
 export function setAdcData(partData) {
    AdcPDB.data = partData;
    // Filter is no good right now: Of course the data is in the PDB, we just added it
    // let filteredData = AdcPDB.data.filter(p=>inAdcPDB(p["Part Number"]))
}

/**
 * Initialize Instrumentation Amps with CDP data.
 * @param {Object[]} partData A list of Parts that use the Simulation Library's expected format: parameter 0 is "Part Number", 4455 is "cCm", and so on.
 */
 export function setInAmpData(partData) {
    // Converting the data is the responsibility of the calling application
    InAmpPDB.data = partData;
    let filteredData = partData.filter(p=>isPartAllowed(p["Part Number"], PART_TYPES.IN_AMP));
    InAmpPDB.data = filteredData; 
}

/**
 * Initialize References with CDP data.
 * @param {Object[]} partData A list of Parts that use the Simulation Library's expected format: parameter 0 is "Part Number", 4995 is "vOut", and so on.
 */
 export function setReferenceData(partData){
    ReferencePDB.data = partData;
}
/**
 * Get list of valid, accepted Op Amps
 * @returns {Object[string]} List of Op Amps stored from the PDB data that was received (invalid amps are filtered out)
 */
export function getOpAmpParts() {
    const opAmpParts = getParts(PART_TYPES.OP_AMP);
    return opAmpParts;
}

//Same as getOpAmpParts(), but universal
export function getParts(partType) {
    switch(partType) {
        case PART_TYPES.OP_AMP:
            return OpAmpPDB.data.map(p=>p["Part Number"]).filter(p=>p!=='ADA4351-2'); //Better way to filter in the future?
        case PART_TYPES.TIA_OP_AMP:
            return OpAmpPDB.data.map(p=>p["Part Number"]);
        case PART_TYPES.DIFF_AMP:
            return DifferentialAmpPDB.data.map(p=>p["Part Number"]);
        case PART_TYPES.IN_AMP:
            return InAmpPDB.data.map(p=>p["Part Number"]);
        case PART_TYPES.REFERENCE:
            return ReferencePDB.data.map(p=>p["Part Number"]);
        case PART_TYPES.ADC:
            return AdcPDB.data.map(p=>p["Part Number"]);
        default: 
            return undefined;       
    }
}

/****************************************************** Public Amp Functions *******************************************/

/**
 * Return PDB and TPC data for an Op Amp
 * @param {string} partName Part name
 * @returns A part object with an extra tpcData property where TPC data is stored
 */
//This code called by other modules in the simulation library, but not by Nimble
export function createOpAmpWithPDBAndTPCData(partName) {
    let opAmp = createPartWithPDBAndTPCData(partName, PART_TYPES.OP_AMP)
    return opAmp;
}

/**
 * Return PDB and TPC data for a Differential Amp
 * @param {string} partName Part name
 * @returns A part object with an extra tpcData property where TPC data is stored
 */
//This code called by other modules in the simulation library, but not by Nimble
export function createDifferentialAmpWithPDBAndTPCData(partName) {
    let differentialAmp = getPDBParametersFor1Part(partName, PART_TYPES.DIFF_AMP);
    differentialAmp.tpcData = UniversalTPC.getPart(partName, PART_TYPES.DIFF_AMP);
    return differentialAmp;
}

//#region Universal part functions

function getTPCDatabase(partType) {
    switch (partType) {
        case PART_TYPES.ADC: return AdcTPCDatabase;
        case PART_TYPES.DIFF_AMP: return DifferentialAmpTPCDatabase;
        case PART_TYPES.IN_AMP: return InAmpTPCDatabase;
        case PART_TYPES.OP_AMP: 
        case PART_TYPES.TIA_OP_AMP: return OpAmpTPCDatabase;
        case PART_TYPES.REFERENCE: return ReferenceCustomDatabase; //This is redudant to provide a map for references
        default: return null;
    }
}

function getCustomDatabase(partType) {
    switch (partType) {
        case PART_TYPES.ADC: return AdcCustomDatabase;
        case PART_TYPES.DIFF_AMP: return DifferentialAmpCustomDatabase;
        case PART_TYPES.IN_AMP: return InAmpCustomDatabase;
        case PART_TYPES.OP_AMP: 
        case PART_TYPES.TIA_OP_AMP: return OpAmpCustomDatabase;
        case PART_TYPES.REFERENCE: return ReferenceCustomDatabase;
        case PART_TYPES.DIODE : return InputProtectionCustomDatabase;
        default: return null;
    }
}

function hasCustomdata(partName, partType) {
    let customDatabase = getCustomDatabase(partType);
    if (!customDatabase) return false;
    return customDatabase.data.map[partName] !== undefined;
}

/**
 * Checks whether a part has TPC data.
 * In amps must have both standard and custom data to pass.
 * @param {string} partName The name of any part
 * @param {*} partType One of the PART_TYPES enumeration: ADC, OP_AMP, etc. 
 * @returns {boolean} True if in database.
 */
//Exporting for testing purposes - this is a private function
export function hasTPCdata(partName, partType) {
    let checkTPC = getTPCDatabase(partType);
    if (!checkTPC) return false;
    return checkTPC.data.map[partName] !== undefined;
}


/**
 * Is a particular part valid based on its partType?
 * @param {string} partName The name of any part
 * @param {*} partType One of the PART_TYPES enumeration: ADC, OP_AMP, etc. 
 * @returns {boolean} True if part is valid
 */
export function isPartAllowed(partName, partType) {
    // first check if part has custom data that specifies which TPC parameters are required 
    let tpcDataRequired = true;
    
    if (partType === PART_TYPES.ADC) {
        tpcDataRequired = false;
        
        //Only let ADCs with our standard device architectures through
        let deviceArchitecture = getDataFor1Part1Parameter(partName, partType, "Device Architecture - (P4364)");
        if(!(["SAR", "Sigma Delta", "Sigma-Delta", "Simultaneous Sigma Delta"].includes(deviceArchitecture))) {
            console.warn(`${partType} ${partName} has an invalid Device Architecture - (P4364) (${deviceArchitecture})`);
            return false;
        } 
        
        // Check to make sure ADC has required custom data items:
        if (!hasCustomdata(partName, partType)) return false;
        
        let customData = UniversalCustom.getPart(partName, partType);
        let inputType = getDataFor1Part1Parameter(partName, partType, "inputType");

        // uModules do not need input range data since it's handled by the internal components
        let inputRangeDataRequired = [];
        if (!partName.includes('ADAQ')) {
            inputRangeDataRequired = inputType === 'Differential' ? ['inputRange.absolute', 'inputRange.commonMode'] : ['inputRange.absolute'];
        }

        let customDataRequired = [
            'requiredSupplyPins', 
            { numRequired: 1, possibleValues: ['supplyCurrent', 'supplyCurrentMap']},
            ...inputRangeDataRequired
        ];

        for (let parameter of customDataRequired) {
            if (!hasParameter(customData, parameter)) return false;
        }

    } else if (hasCustomdata(partName, partType)) {
        let customData = UniversalCustom.getPart(partName, partType);
        let requiredTPCParameters = customData?.requiredTPCParameters;
        if (Array.isArray(requiredTPCParameters) && requiredTPCParameters.length === 0) {
            tpcDataRequired = false;
        } else if (requiredTPCParameters) {
            // If custom data required - check that specified parameters are present in part's TPC data
            let tpcData = UniversalTPC.getPart(partName, partType);
            if (!tpcData) {
                return false;
            }   
            let tpcDataElements = tpcData ? Object.keys(tpcData) : [];
            for (let param of requiredTPCParameters) {
                if (!tpcDataElements.includes(param)) {
                    return false;
                }
            }
        }
    } else if ( partType === PART_TYPES.IN_AMP) {
        // in-amps always require custom data, so if it made it this far, it's an in-amp without custom data and is not allowed
        return false;      
    }
    
    //Weed out most parts here
    if (tpcDataRequired && !hasTPCdata(partName, partType)) {
        return false;
    }

    //Removes parts with all neceesary data that we want to exclude for some other reason
    if (RemovedParts.data.includes(partName)) {
        return false;
    }

    return doesPartHaveRequiredPDBParameters(partName, partType)

}

/**
 * Create a part with PDB/TPC data based on its type and name
 * @param {string} partName The name of any part
 * @param {*} partType One of the PART_TYPES enumeration: ADC, OP_AMP, etc. 
 * @returns Part object, with PDB data in the root, a tpcData property, and an optional customData property for irregular data that can't be stored in tpcData.
 */
export function createPartWithPDBAndTPCData(partName, partType) {
    // For now, references are handled differently since there are not yet any PDB or TPC databases
    //if (partType === PART_TYPES.REFERENCE) return createReferenceWithPDBAndTPCData(partName);
    let part = getPDBParametersFor1Part(partName, partType);
    
    part.tpcData = UniversalTPC.getPart(partName, partType);
    let customData = UniversalCustom.getPart(partName, partType);
    if (customData) part.customData = customData;
    
    return part;
}

/**
 * Determine the part type of partName by searching the part PDBs
 * @param {string} partName The name of any part
 * @param {array} possiblePartTypes (optional) Array of possible types to check partName against
 * @returns {string} The type of part it is, string should match PART_TYPES sim constants
 */
export function getPartType(
    partName, 
    possiblePartTypes = 
    [
        PART_TYPES.ADC,
        PART_TYPES.OP_AMP,
        PART_TYPES.TIA_OP_AMP,
        PART_TYPES.DIFF_AMP,
        PART_TYPES.IN_AMP,
        PART_TYPES.REFERENCE,
        PART_TYPES.DIODE,
    ]
    ) {

    let partType;
    for(let i = 0; i< possiblePartTypes.length; i++) {
        try{
            if(inPDB(partName, possiblePartTypes[i])) {
                partType = possiblePartTypes[i];
            }
        } catch {
            try{ //diodes are not in PDB but have a custom database
                if(hasCustomdata(partName, possiblePartTypes[i])){
                    partType = possiblePartTypes[i];
                }
            } catch{
                continue;
            }
        }
    }
    return partType;
}

/*
 *  Map a component name (i.e. opAmp, differentialAmp, refBuf etc., not the part name 'ADA4510-2') to the part type
 *  
 */
export function mapComponentNameToPartType(componentName) {
    
    const PART_TYPE_MAP = {
        [COMPONENT_NAMES.OP_AMP] : PART_TYPES.OP_AMP,
        [COMPONENT_NAMES.IN_AMP] : PART_TYPES.IN_AMP,
        [COMPONENT_NAMES.DIFF_AMP] : PART_TYPES.DIFF_AMP,
        [COMPONENT_NAMES.ADC] : PART_TYPES.ADC,
        [COMPONENT_NAMES.REFERENCE] : PART_TYPES.REFERENCE,
        [COMPONENT_NAMES.REF_BUF] : PART_TYPES.OP_AMP,
        [COMPONENT_NAMES.DIODE] : PART_TYPES.DIODE,
        [COMPONENT_NAMES.DRIVER] : [PART_TYPES.OP_AMP, PART_TYPES.DIFF_AMP],
    }
    
    let partType;
    let componentBaseName = componentName.replace(/\d+$/, "");
    try {
        partType = PART_TYPE_MAP[componentBaseName];
    } catch {
        return undefined;
    }

    return partType;
}

//#endregion Universal part

/******************************* Public Reference Functions***************************** */

/**
 * Return PDB and TPC data for References. 
 * **Despite the name, this function doesn't populate anything from PDB or TPC's today, but we're set up for the future.**
 * @param {string} partName Part name
 * @returns A reference object with {partName, vOut}.
 */
//Exporting for testing purposes - this is a private function
export function createReferenceWithPDBAndTPCData(partName) {
    //let part = getPDBParametersFor1Part(partName, "bgRef");
    let voltage;

    let LT6657Re = /(LT6657)+([a-zA-Z0-9]*)+(-)+[0-9.]*/;
    let isLT6657 = LT6657Re.test(partName);
    let LTC6655Re = /(LTC6655)+([a-zA-Z0-9]*)+(-)+[0-9.]*/;
    let isLTC6655 = LTC6655Re.test(partName);
    let LTC6655LNRe = /(LTC6655LN)+([a-zA-Z0-9]*)+(-)+[0-9.]*/;
    let isLTC6655LN = LTC6655LNRe.test(partName);

    let partNameReduced;
    if      (isLT6657)    partNameReduced = 'LT6657';
    else if (isLTC6655LN) partNameReduced = 'LTC6655LN';
    else if (isLTC6655)   partNameReduced = 'LTC6655';
    else throw new Error(partName + " is not a supported reference");

    voltage = parseFloat(partName.split('-')[1]);
    partNameReduced = `${partNameReduced}-${voltage}`;
    
    return {
        partName: partNameReduced,
        vOut: voltage
    };
}

/******************************* Public ADC Functions***************************** */

/**
 * Return PDB data for one ADC.
 * @param {string} partName Part name
 * @returns A part object with CDP data exclusively
 */
//This code called by other modules in the simulation library, but not by Nimble
export function createAdcWithPDBData(partName) {
    let adc = getPDBParametersFor1Part(partName, PART_TYPES.ADC);
    return adc;
}
/****************************************************** Private PDB Functions *******************************************/

//Check if part has data for each required PDB parameter
function doesPartHaveRequiredPDBParameters(partName, partType) {
    let part = getPDBParametersFor1Part(partName, partType);
    let partCustomData = getPartFromCustomData(partName, partType);
    // If a part has custom data that specifies the required PDB parameters, only require those parameters.
    //  Otherwise, get the required parameters from the standard list by partType
    let requiredParameters;
    if (partCustomData?.requiredPDBParameters) {
        requiredParameters = partCustomData?.requiredPDBParameters;
    } else {
        requiredParameters = getPDBParameterList(partName, partType, true, false);
    }

    let pdbGood = true;

    //Declare key and pdbParameter outside the loop so it's easy to debug when a part fails
    let key = "";
    for (let i=0; i<requiredParameters.length; i++) {
        key = requiredParameters[i];
        if ((part[key] === undefined) || (part[key]  === "(NVR)")) {
            // suppress console.log if running in QUnit CLI / yarn test
            //if (typeof navigator !== "undefined") console.warn(`${partType} ${part.partName} is missing required param (${key})`);
            pdbGood = false;
            break;
        }
    }
    return pdbGood;
}


//Returns the list of parameters we want to support for a particular part type
//This just returns a parameter list, not any actual data
//If "required" is TRUE, returns list of parameters part must have to be included
//If "optional" is TRUE, returns list of parameters that we'd like the part to have, but we'll use defaults if not available
function getPDBParameterList(partName, partType, required, optional) {
    let requiredParameters, optionalParameters;

    if ((partType === PART_TYPES.OP_AMP) || (partType === PART_TYPES.TIA_OP_AMP) ) {
        requiredParameters = ["cDiff", "cCm", "rOut", "vNoise", "iNoise", "gbw", "slewRate"];
        optionalParameters = ["rDiff", "rCm", 
            "outHeadroomVminusTyp", 
            "outHeadroomVplusTyp", 
            "inHeadroomVminusTyp", 
            "inHeadroomVplusTyp", 
            "outHeadroomVminusMax", 
            "outHeadroomVplusMax", 
            "inHeadroomVminusMax", 
            "inHeadroomVplusMax",
            "vos",
            "vosAt85C",
            "vosAtMaxTemp",
            "vosTCAt25C",
            "vosTCAt85C",
            "vosTCAtMaxTemp",
            "iBias",
            "iBiasMax",
            "iBiasAt85C",
            "iBiasAtMaxTemp",
            "iBiasTCAt85C",
            "iBiasTCAtMaxTemp",
            "ios",
            "iosAt85C",
            "iosAtMaxTemp",
            "iosTCAt85C",
            "iosTCAtMaxTemp",
            "aol",
            "cmrr",
            "tempMin",
            "tempMax",
            "supplyCurrentTyp",
            "supplyCurrentMax",
            "inputArchitecture"
        ];
    }
    if (partType === PART_TYPES.DIFF_AMP) {
        requiredParameters = ["cDiff", "cCm", "rOut", "vNoise", "iNoise", "gbw", "slewRate"];
        optionalParameters = ["rDiff", "rCm", "vNoiseVocm", "cF", "gbwCm", "rInVocm", "gainSet", 
            "outHeadroomVminusTyp", 
            "outHeadroomVplusTyp", 
            "inHeadroomVminusTyp", 
            "inHeadroomVplusTyp", 
            "vocmHeadroomVplusTyp", 
            "vocmHeadroomVminusTyp", 
            "outHeadroomVminusMax", 
            "outHeadroomVplusMax", 
            "inHeadroomVminusMax", 
            "inHeadroomVplusMax", 
            "vocmHeadroomVplusMax", 
            "vocmHeadroomVminusMax",
            "vos",
            "vosAt85C",
            "vosAtMaxTemp",
            "vosTCAt25C",
            "vosTCAt85C",
            "vosTCAtMaxTemp",
            "iBias",
            "iBiasMax",
            "iBiasAt85C",
            "iBiasAtMaxTemp",
            "iBiasTCAt85C",
            "iBiasTCAtMaxTemp",
            "ios",
            "iosAt85C",
            "iosAtMaxTemp",
            "iosTCAt85C",
            "iosTCAtMaxTemp",
            "aol",
            "cmrr",
            "tempMin",
            "tempMax",
            'gainSet',
            "supplyCurrentTyp",
            "supplyCurrentMax",
        ];
    }
    if (partType === PART_TYPES.IN_AMP) {
        //TO DO: in amp modelers to fill these in
        requiredParameters = ["vNoise", "iNoise", "gbw", "slewRate", "ampArchitecture"];
        optionalParameters = [
            "cDiff", 
            "cCm", 
            "rDiff", 
            "rCm", 
            "vNoiseVocm", 
            "gbwO", 
            "vNoiseI", 
            "vNoiseO", 
            "cF", 
            "gainMin", 
            "rF", 
            "rOut",
            "gainSet", 
            "gainMax",
            "gain1",
            "gain2",
            "gain3",
            "gain4",
            "gain5",
            "cmrr1",
            "cmrr2",
            "cmrr3",
            "cmrr4",
            "cmrr5",
            "vos1",
            "vos2",
            "vos3",
            "vos4",
            "vos5",
            "vosTC1",
            "vosTC2",
            "vosTC3",
            "vosTC4",
            "vosTC5",
            "iBias",
            "iBiasAt85C",
            "iBiasAtMaxTemp",
            "iBiasTCAtMaxTemp",
            "iBiasTCAt85C",
            "ios",
            "iosAt85C",
            "iosAtMaxTemp",
            "iosTCAt85C",
            "iosTC",
            "gainMin",
            "gainErrorHighGain",
            "gainErrorHighGainAt85C",
            "gainErrorHighGainAtMaxTemp",
            "gainTCHighGainAt85C",
            "gainTCHighGain",
            "gainErrorLowGain",
            "gainErrorLowGainAt85C",
            "gainErrorLowGainAtMaxTemp",
            "gainTCLowGainAt85C",
            "gainTCLowGain",
            "vosi",
            "vosiAt85C",
            "vosiAtMaxTemp",
            "vosiTCAt85C",
            "vosiTCAtMaxTemp",
            "voso",
            "vosoAt85C",
            "vosoAtMaxTemp",
            "vosoTCAt85C",
            "vosoTCAtMaxTemp",
            "tempMin",
            "tempMax",
            "supplyCurrentTyp",
            "supplyCurrentMax",
            "inputArchitecture",

        ];

        //Add diamond data specific to architecture. ampArchitecture can be one of ['classic current feedback','2 op amp','voltage feedback PGA','indirect current feedback', 'charge sampling', '']
        let deviceArchitecture = getDataFor1Part1Parameter(partName, partType, "ampArchitecture");
        
        switch(deviceArchitecture){
            case 'classic current feedback':
                //Parts that don't include all the parameters below: AD8229S, LT1920, LT1167-1 (Same as LT1167?)
                requiredParameters.push(...['preAmpOutputNegMargin', 'preAmpOutputPosMargin', 'vSubtractorNeg', 'vSubtractorPos', 'levelShift','outHeadroomVminusMax', 'outHeadroomVplusMax', 'inHeadroomVminusMax', 'inHeadroomVplusMax']);
                break;
            case '2 op amp':
                requiredParameters.push(...['preAmpOutputNegMargin', 'preAmpOutputPosMargin', 'levelShift']);
                break;
            case 'voltage feedback PGA':
                //Parts that don't include all the parameters below: MAX41400
                requiredParameters.push(...['preAmpOutputNegMargin', 'preAmpOutputPosMargin', 'vSubtractorNeg', 'vSubtractorPos', 'levelShift', 'outHeadroomVminusMax', 'outHeadroomVplusMax', 'inHeadroomVminusMax', 'inHeadroomVplusMax']);
                break;
            case 'indirect current feedback':
                //Parts that don't include all the parameters below: AD8233, AD8232
                requiredParameters.push(...['levelShift']);
                break;
            case 'charge sampling':
                requiredParameters.push(...[]);
                break;
            default:
                //Parts that don't include all the parameters below (blank architecture): LTC6373 (Should be added to VF PGIA?), ADA4254/5 (Custom model with interal PDB) 
                //There are other parts too in PST (12860 - Instrumentation Amps - Nimble - Tools), but they are not in Nimble (e.g MAX...) 
                //else do nothing
        }

    }
    if (partType === PART_TYPES.ADC) {
        requiredParameters = [
            "Resolution (bits) - (P7)",
            "Sample Rate (max) (SPS) - (P1746)",
            "Channels - (P3062)",
            "inputType",
            "INL in LSB (typ) (LSBs) - (P4748)",
            "Bipolar or Unipolar Input - (P5430)",
            "Vref External (max) (V) - (P4914)",
            "Vref External (min) (V) - (P4915)",
            "VrefAtSNR",
            "DefaultReference",
            "ReferenceCap",
            "Device Architecture - (P4364)"
        ];
        optionalParameters = [
            "cSample",
            "rSample",
            "SNR",
            "RMSnoise",
            "wideCommonMode",       // I don't see anywhere this is not null
            "filterCapacitor",  // "Filter capacitor (F) - (P6729)",
            "filterResistor",  // "Filter resistor (Ohms) - (P6730)",
            "UseNoRCSchematic",
            "InternalInputFDA",
            "zeroScaleErrorAt25C",
            "zeroScaleErrorAtMaxTemp",
            "zeroScaleErrorDrift",
            "fullScaleErrorAtMaxTemp",
            "fullScaleErrorAt25C",
            "fullScaleErrorDrift",
            "inlErrorAt25C",
            "inlErrorAtMaxTemp",
            "inlErrorDrift",
            "iBias",
            "iBiasAtMaxTemp",
            "iBiasTCAtMaxTemp",
            "vRefInitialAccuracy",
            "vRefAccuracyOverTemp",
            "vRefTC",
            "tempMin",
            "tempMax",
            "resolution",
            "inputStageVDD",
            "inputStageVSS",
            "inHeadroomVplusTyp",
            "inHeadroomVminusTyp",
        ];

        //Conditional required parameters.  These depend on data from other parameters.
        let internalInputFDA = getDataFor1Part1Parameter(partName, partType, "InternalInputFDA");
        let useNoRCSchematic = getDataFor1Part1Parameter(partName, partType, "UseNoRCSchematic");
        let deviceArchitecture = getDataFor1Part1Parameter(partName, partType, "Device Architecture - (P4364)");
        if(!internalInputFDA && !useNoRCSchematic) {
            requiredParameters.push(...["filterCapacitor", "filterResistor"]);
        }
        if(!internalInputFDA && deviceArchitecture) {
            requiredParameters.push(...[
                // "Power (typ) (W) - (P3970)",
                "RMSnoise"
            ]);
        }
    }
    if (partType === PART_TYPES.REFERENCE){
        requiredParameters = [
            "vOut",
        ];
        optionalParameters = [
            "supplyCurrentTyp",
            "supplyCurrentMax",
            "vOutTempCo",
            "initialAccuracy",
            "loadRegulation",
        ];
    }
    if (partType === PART_TYPES.DIODE) {
        requiredParameters = [];
        optionalParameters = [];
    }

    let parameterList = [];
    if (required) {
        parameterList = parameterList.concat(requiredParameters);
    }
    if (optional) {
        parameterList = parameterList.concat(optionalParameters);
    }
    return parameterList;
}

//Gets both the required and optional parameters for a part
function getPDBParametersFor1Part(partName, partType) {

    let pdbParameters = getPDBParameterList(partName, partType, true, true);
    let partObject = {partName: partName};
    for (let i=0; i<pdbParameters.length; i++) {
        let pdbParameter = pdbParameters[i];
        partObject[pdbParameter] = getDataFor1Part1Parameter(partName, partType, pdbParameter);
    }
    return partObject;
} 

//Get the data for one PDB parameter for one part
function getDataFor1Part1Parameter(partName, partType, pdbParameter) {
    if (!inPDB(partName, partType)) {
        return undefined;
    }

    let partDatabase = getPartPDBDatabase(partType);
    let part = partDatabase.data[pdbPartIndex(partName, partType)];
    // Added OR to use pdbParameter if no map item exists
    let pdbParameterName = pdbParameter;
    let specData = part[pdbParameterName];

    if (pdbParameter === "rDiff") {
        if ((specData === undefined) || (specData === "(NVR)")) {
            specData = 1e12;
        }
    }

    if (pdbParameter === "slewRate") {
        if ((specData === undefined) || (specData === "(NVR)")) {
            specData = 1e12;
        }
        else {
            specData = specData * 1e6;
        }
    }

    if (pdbParameter === "cF") {
        if ((specData === undefined) || (specData === "(NVR)")) {
            specData = 1e-20;
        }
    }

    if (pdbParameter === "rInVocm") {
        if ((specData === undefined) || (specData === "(NVR)")) {
            specData = 1e12;
        }
    }

    if (pdbParameter === "gbwCm") {
        if ((specData === undefined) || (specData === "(NVR)")) {
            specData = 1e9;
        }
    }
    
    return specData;
}


/**
 * Checks if part is in our PDB database 
 * @param {string} partName The name of any part
 * @param {*} partType One of the PART_TYPES enumeration: ADC, OP_AMP, etc. 
 * @returns {boolean} True if available
 */
//Exporting for testing purposes - this is a private function
export function inPDB(partName, partType) {
    let partIndex = pdbPartIndex(partName, partType);
    if (partIndex === undefined) {
        return false;
    }
    else {
        return true;
    }
}

//Return array index of the part in its corresonding amp pdb database (OpAmpPDB, InAmpPDB, AdcPDB, etc.)
function pdbPartIndex(partName, partType) {
    let partDatabase = getPartPDBDatabase(partType);
    let partIndex = undefined;
    for (let i=0; i<partDatabase.data.length; i++) {
        if (partDatabase.data[i]["Part Number"] === partName) {
            partIndex = i;
            break;
        }
    }
    return partIndex;
}

function getPartPDBDatabase(partType) {
    switch (partType) {
        case PART_TYPES.OP_AMP:
            return OpAmpPDB;
        case PART_TYPES.DIFF_AMP:
            return DifferentialAmpPDB;
        case PART_TYPES.ADC:
            return AdcPDB;
        case PART_TYPES.IN_AMP:
            return InAmpPDB;
        case PART_TYPES.REFERENCE:
            return ReferencePDB;
        default:
            return undefined;
    }
}

//Checks if part has LTspice support
function _hasLTspice(partName, partType) {
    let hasLTspice = false;
    if (inPDB(partName, partType)) {
        let part = getPDBParametersFor1Part(partName, partType);
        if (part.designTools) {
            hasLTspice = part.designTools.includes("LTspice");
        }
    }
    return hasLTspice;
}

// Determine if data object has a given parameter in its keys
// parameter can be:
//  1. A simple string to check if dataObj has an element with key === parameter (i.e. dataObj.parameter exists)
//  2. A string with dot notation for complex objects.  (i.e. parameter = 'elem1.elem2' to check if dataObj.elem1.elem2 exists)
//  3. An object with {numRequired: integer, possibleValues: Array of parameters (in any of the formats listed, including this one (object))} 
/**
 * Determine if a data object (PDB, TPC, or Custom data) has a parameter in its keys
 * @param {Object} dataObj Data Object with key/value pairs.  Can have nested data in sub-objects
 * @param {string|Object} parameter String to search for in dataObj keys, including sub-keys given with dot notation.  Alternatively, an Object to search for a certain number of parameters out of a list of possibilities
 * @param {number} [parameter.numRequired] Number of parameters required out of the possibleValues array
 * @param {string[]} [parameter.possibleValues] Array of strings to search for in dataObj keys, including sub-keys given by dot notation.
 * @returns {Boolean} true if found, false if not
 */
export function hasParameter(dataObj, parameter) {

    if (typeof parameter === 'string') {
        let keys = parameter.split('.');
        let topKey = keys[0];
        
        let subKeys = keys.slice(1);
        if (subKeys.length > 0) {
            return hasParameter(dataObj[topKey], subKeys.join('.'));
        }

        return !!dataObj?.[topKey];
    }

    if (typeof parameter === 'object') {
        const { numRequired, possibleValues } = parameter;
        let numFound = 0;
        for (let param of possibleValues) {
            numFound += hasParameter(dataObj, param) ? 1 : 0;
            if (numFound >= numRequired) return true;
        }

        return false;
    }
}

/**
 * Determine is a parameter contains any data
 * @param {*} parameter 
 * @returns {boolean} if null, undefined
 */
export function parameterHasData(parameter) {
    if (parameter || parameter === 0) { // should we be allowing 'false' to also be data?  unit tests break when I add false as data
        return true;
    } else {
        return false;
    }
}

/********************************************   Private Op Amp functions **************************************/


//Use an IIFE plus closure to be able to store previously accessed parts so we can access them quickly rather than traversing the database each time 
function universalTPCIIFE(dataGetter) {
    //Object only containing the data of parts we've accessed before
    let smallData = {};
    return {
        getPart: function (partName, partType) {
            let key = partType + "|" + partName;
            if (smallData[key]) {
                return smallData[key];
            }
            else {
                let newPart = dataGetter(partName, partType);
                smallData[key] = newPart;
                return newPart;
            }
        }
    }
}

let UniversalTPC = universalTPCIIFE(getPartFromRawTPCData);
let UniversalCustom = universalTPCIIFE(getPartFromCustomData);
   
function getPartFromRawTPCData(partName, partType) {
    let tpcDatabase = getTPCDatabase(partType);
    let datasheet = tpcDatabase?.data.map[partName];
    let tpcData = tpcDatabase?.data.partData[datasheet];
    return tpcData;
}

function getPartFromCustomData(partName, partType) {
    // Custom data - manually entered custom data not directly from datasheet TPC curves
    let customDatabase = getCustomDatabase(partType);
    let customData;
    if (customDatabase !== null) {
        let datasheet = customDatabase.data.map[partName];
        customData = customDatabase.data.partData[datasheet];
    }
    return customData;
}

